import PropTypes from 'prop-types';
import styled, { css } from 'styled-components/macro';
import { a_labHovered, active_project, current_language, currentActiveProject, isMulti_project_active, iso_free, iso_isrotating, params_query, vis_overlay } from 'state/store.global';
import { active_unit } from 'state/store.global';
import { useGet, useAtom } from 'state/jotai';
import { AnimatePresence } from 'framer-motion';
import { open_drawer } from 'state/actions';
import { useNavigate } from 'react-router-dom';

import React, { useEffect, useMemo, useState, memo } from 'react';
import { useBreak } from 'hooks/useBreak';
import { _bootstrap } from 'state/store.bootstrap';
import { getIsoData, getLowIsoData } from 'state/jotai.isometryDataHooks';
import { getProjectData } from 'state/jotai.projectDataHooks';
import { Overview } from 'components/OverviewLabel/Overview';
import { InnerIsometryLabels } from 'components/IsometryLabels/InnerIsometryLabels';
import { useSendClick } from 'hooks/fetch/useSendClick';
import { useToggle } from 'hooks/useToggle';

export const Svg = memo(({ svg, percent, units, showTooltip, hideTooltip }) => {
  const { pin, pid } = useGet(active_project);
  const multiActive = useAtom(isMulti_project_active)[0];
  const setActiveProject = useAtom(currentActiveProject)[1];
  const is_labHovered = useAtom(a_labHovered)[0];
  const type = useGet(_bootstrap).type;
  const vbox = svg?.attributes?.viewBox;
  const blur = Math.abs(percent - 100) / 3;
  const opac = percent / 100;
  const draw = useAtom(open_drawer)[1];
  const geta = useAtom(active_unit)[0];
  const seta = useAtom(active_unit)[1];
  const show = useGet(vis_overlay);
 const { toggleOverlay } = useToggle();
  const picks = getProjectData('handpicked') ?? [];
  const pickSet = useMemo(() => new Set(picks), [picks]);

  const normalizedUnits = useMemo(() => {
    if (Array.isArray(units)) return units;
    if (units && typeof units === 'object') return Object.values(units);
    return [];
  }, [units]);

  const unitsBySvgId = useMemo(
    () => new Map(
      normalizedUnits.map((item) => {
        const key = item?.Data?.SVGID?.UnformattedValue?.toString();
        return [key, item];
      }),
    ),
    [normalizedUnits],
  );

  const releasedUnits = getProjectData('unitsraw', 'Units', []);
  const releasedSvgIds = useMemo(
    () => new Set(releasedUnits.map((item) => item?.Data?.SVGID?.UnformattedValue?.toString())),
    [releasedUnits],
  );

  const getUnitValue = (ID, key, mode = 'u') => {
    const unitData = unitsBySvgId.get(ID);
    if (!unitData) return '';
    if (mode === 'i') return unitData;
    const field = unitData?.Data?.[key];
    if (!field) return '';
    return mode === 'f' ? field.Value ?? '' : field.UnformattedValue ?? '';
  };

  const params = useGet(params_query);
  const language = useAtom(current_language)[0];
  const index = getIsoData('snapindex');
  const rmode = useGet(iso_free);
  const navigate = useNavigate();
  const isMobile = useBreak('md_dn');
  const [showLabel, setShowLabel] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [hoveredPos, setHoveredPos] = useState(null);
  const isrotating = useGet(iso_isrotating);

  const sendClick = useSendClick();

  const lowQualityStatus = getLowIsoData('status');

  useEffect(() => {
    setShowLabel(false);
  }, [pin, pid]);

  return (
    <>
      <AnimatePresence>
        {units && percent === "100" && show && (
          <svg viewBox = {vbox} style = {{ position: 'absolute', willChange: 'transform' }}>
            {
              svg?.children?.map(({ attributes: { id, d } }, index) => {
                const ID = id?.replace(/nr/g, '').split('-')[0];
                const isUnitShape = !isNaN(ID);
                if (isUnitShape && !releasedSvgIds.has(ID)) return null;
                const sID = isNaN(ID) ? '' : getUnitValue(ID, 'UnitStatusID');
                const sName = isNaN(ID) ? '' : getUnitValue(ID, 'UnitStatusName');
                let color = isNaN(ID) ? '#ffffff00' : getUnitValue(ID, 'UnitColor', 'f');
                if (ID === 'Kommer_snart') {
                  color = '#443F3980';
                }
                const picked = isNaN(ID) ? '' : pickSet.has(ID);
                const UnitID = isNaN(ID) ? '' : getUnitValue(ID, 'UnitID');
                const Unit = isNaN(ID) ? '' : getUnitValue(ID, 0, 'i');
                const isActive = geta?.UnitID === UnitID;
                const onClick = (e) => {
                  e.stopPropagation();
                  if (ID === 'Kommer_snart') {
                    isMobile && showTooltip(e);
                    return null;
                  }

                  // if (type === 'poly' && pin === 0 && isMobile) {
                  //   setShowLabel(true);
                  // } else {
                    if (UnitID === false) return;

                    if (isNaN(ID)) {
                      if (ID === 'c6' || ID === 'c4') {
                        setActiveProject(ID);
                        navigate(`/${language}/isometry/${ID}${params.length ? params : ''}`);
                      }
                    }

                    if (sID == 40 || sID == 30) return;

                    if (sID !== 40 && multiActive) {
                      seta(Unit);
                       toggleOverlay('detail', { open: true } );
                       sendClick("unit_clicked", { unitID: UnitID });
                    }
                  }
                // };

                const style = { filter: `blue(${blur}px)`, opacity: isNaN(ID) ? '1' : opac };
                const path = { d, id, style, onClick, sID, sName, color, picked, active: isActive, ID, is_labHovered };

                if (!d.includes('M0'))
                  return (
                    <Path
                      key={`${id ?? 'path'}-${index}`}
                      {...path}
                      pin={pin}
                      onMouseEnter={(evt) => {
                        !isMobile && setShowLabel(true);
                        !isMobile && ID === 'Kommer_snart' && showTooltip(evt);
                        setHoveredId(ID); // we don't need label on hover for mobile
                        setHoveredPos({ x: evt.clientX, y: evt.clientY });
                      }}
                      onMouseLeave={() => {
                        !isMobile && hideTooltip();
                        setShowLabel(false);
                        setHoveredId(null);
                        setHoveredPos(null);
                      }}
                      onMouseMove={(evt) => {
                          setHoveredId(ID);
                        if (!isMobile) {
                          setHoveredPos({ x: evt.clientX, y: evt.clientY });
                        }
                      }}
                    />
                  );
              })}
          </svg>
        )}
      </AnimatePresence>
      {
        !isrotating &&(
        <Overview {...{ index, rmode, status: lowQualityStatus, showLabel, setShowLabel, hoveredId }} />
      )}
       {!isrotating && (
        <InnerIsometryLabels
          {...{
            index,
            rmode,
            status: lowQualityStatus,
            showLabel,
            setShowLabel,
            hoveredPos,
            hoveredId,
          }}
        />
      )}
    </>
  );
});

Svg.displayName = 'Svg';
Svg.propTypes = {
  svg: PropTypes.object,
  percent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  units: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  showTooltip: PropTypes.func,
  hideTooltip: PropTypes.func,
};

const Path = styled.path(
  ({ sID, color, picked, active, ID, is_labHovered }) => css`

    cursor: ${(() => {
      // if (ID === "bf03" || ID === "bf04") return "allowed";
      return sID == 40 ? "not-allowed" : "pointer";
    })()};

    fill: ${(() => {
      if (
        typeof is_labHovered?.name === "string" &&
        is_labHovered?.name?.toUpperCase() === ID?.toUpperCase()
      )
        return "#ffffff80";
      if (active) return "#FFFFFF90";
      if (picked) return "#FF00FF";
      if (color?.value == "err") {
        if (sID == 10) return "#00ff0080";
        if (sID == 20) return "#ffff0080";
        if (sID == 30) return "#0000ff80";
        if (sID == 40) return "#ff000080";
      }
      if (isNaN(ID)) {
        return color;
      }
      return typeof color === "string" && color.length > 3 ? color + "80" : "#00000080";
    })()};

    ${typeof color !== "string"} {
      :hover {
        fill: #ffffff80;
      }
    }
  `
);

export default Path;